import { ethers } from "ethers";
import { withDeadline } from "../utils/withDeadline";
import { EVM_CONFIG } from "../config";
import { privatePools } from "./privatePools";
import { deriveSpendingKeys } from "../joinsplit/keys";
import {
  createStoredProxyAuthHeaders,
  proxyAuthNetworkScope,
} from "../utils/proxy-auth";
import {
  exportFlatJournalBackup,
  importFlatJournalBackup,
  assertFlatJournalBackupMatchesRecord,
  archiveFlatJoinSplitV2UndisclosedPreparation,
  type FlatJournalBackup,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";
import { walletSettlementErrorWithContext } from "./walletSubmission";

export interface JournalBackupOptions {
  baseUrl?: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Reuse an authenticated immutable snapshot when retrying the same intent. */
  resume?: boolean;
}

export function journalBackupDigest(backup: FlatJournalBackup): string {
  return ethers.sha256(ethers.toUtf8Bytes(JSON.stringify(backup))).slice(2);
}

async function context(
  scope: FlatJoinSplitV2JournalScope,
  options: JournalBackupOptions,
) {
  const keys = await deriveSpendingKeys(scope.spendingKey);
  const mpk = ethers.toBeHex(keys.mpk, 32).toLowerCase();
  const recipient = ethers.sha256(ethers.toUtf8Bytes(mpk)).slice(2);
  const headers =
    options.headers ??
    createStoredProxyAuthHeaders({
      chain: proxyAuthNetworkScope(),
      wallet: scope.accountAddress,
    });
  if (!headers.Authorization?.startsWith("Bearer ")) {
    throw new Error(
      "Sign in again to authorize encrypted recovery backup before sending",
    );
  }
  const baseUrl = options.baseUrl ?? EVM_CONFIG.PROVER_URL;
  return {
    url: `${baseUrl}/api/v1/recovery/journal/${recipient}`,
    headers: { "Content-Type": "application/json", ...headers },
  };
}

/** Immutable encrypted output secrets must survive BEFORE simulation discloses a proof. */
export async function persistFlatJournalBackup(
  scope: FlatJoinSplitV2JournalScope,
  id: string,
  options: JournalBackupOptions = {},
): Promise<void> {
  try {
    await persistBackup(scope, id, options);
  } catch (error) {
    // This call owns its preparation. A legacy/already exposed attempt remains
    // reserved; a known undisclosed one can safely release its inputs.
    await archiveFlatJoinSplitV2UndisclosedPreparation(scope, id).catch(
      () => {},
    );
    throw walletSettlementErrorWithContext(error, id);
  }
}

async function persistBackup(
  scope: FlatJoinSplitV2JournalScope,
  id: string,
  options: JournalBackupOptions,
): Promise<void> {
  if (options.resume) {
    for await (const saved of readBackups(scope, options)) {
      if (saved.id !== id) continue;
      await assertFlatJournalBackupMatchesRecord(scope, saved, id);
      return;
    }
  }
  const backup = await exportFlatJournalBackup(scope, id);
  const { url, headers } = await context(scope, options);
  const result = await withDeadline(
    async (signal) => {
      const response = await fetch(url, {
        method: "PUT",
        headers,
        body: JSON.stringify(backup),
        signal,
      });
      if (!response.ok)
        throw new Error(
          `Encrypted recovery backup rejected (${response.status}); transaction was not submitted`,
        );
      return response.json();
    },
    "Encrypted recovery backup timed out; transaction was not submitted",
    options.signal,
    30_000,
  );
  if (
    result?.success !== true ||
    result.id !== backup.id ||
    result.digest !== journalBackupDigest(backup)
  ) {
    throw new Error(
      "Recovery backup acknowledgement does not match the encrypted record",
    );
  }
}

/** Restore ciphertext only. The existing reconciler—not the backend—decides chain state. */
export async function restoreFlatJournalBackups(
  scope: FlatJoinSplitV2JournalScope,
  options: JournalBackupOptions = {},
): Promise<number> {
  let restored = 0;
  for await (const backup of readBackups(scope, options))
    if (
      await importFlatJournalBackup(
        scope,
        backup,
        () => !options.signal?.aborted,
      )
    )
      restored += 1;
  return restored;
}

async function* readBackups(
  scope: FlatJoinSplitV2JournalScope,
  options: JournalBackupOptions,
): AsyncGenerator<FlatJournalBackup> {
  const { url, headers } = await context(scope, options);
  let cursor: string | undefined;
  const seen = new Set<string>();
  do {
    if (cursor && seen.has(cursor))
      throw new Error("Recovery backup pagination repeated");
    if (cursor) seen.add(cursor);
    if (seen.size >= 100)
      throw new Error("Recovery backup pagination exceeded limit");
    const query = new URLSearchParams();
    if (
      scope.poolAddress.toLowerCase() !==
        EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT.toLowerCase() &&
      privatePools().some(
        (p) => p.toLowerCase() === scope.poolAddress.toLowerCase(),
      )
    )
      query.set("pool", scope.poolAddress);
    if (cursor) query.set("cursor", cursor);
    const result = await withDeadline(
      async (signal) => {
        const response = await fetch(query.size ? `${url}?${query}` : url, {
          headers,
          signal,
        });
        if (!response.ok)
          throw new Error(
            `Encrypted recovery restore unavailable (${response.status})`,
          );
        return response.json();
      },
      "Recovery service is taking too long. Please try again.",
      options.signal,
    );
    if (
      result?.success !== true ||
      !Array.isArray(result.records) ||
      result.records.length > 100 ||
      (result.nextCursor != null && typeof result.nextCursor !== "string")
    ) {
      throw new Error("Malformed encrypted recovery backup response");
    }
    for (const item of result.records) {
      if (
        !item?.record ||
        item.id !== item.record.id ||
        item.digest !== journalBackupDigest(item.record)
      )
        throw new Error("Recovery backup digest mismatch");
      if (
        item.record.poolAddress?.toLowerCase() !==
        scope.poolAddress.toLowerCase()
      )
        continue;
      yield item.record;
    }
    cursor = result.nextCursor || undefined;
  } while (cursor);
}
